import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

export interface EmployeeProgressBarProps {
  success: number;
  error: number;
  /** Employees the scope covers; null while the range is still provisional. */
  total: number | null;
  /** Shown instead of the bar when there is no total yet. */
  pendingLabel?: string;
  tooltip?: string;
  /** Muted trailing note in the legend, e.g. "1,200 folder(s) in S3". */
  extra?: string;
  onErrorsClick?: () => void;
  showPercent?: boolean;
  /** Content for the left of the top line (chips); the numbers move right. */
  leading?: React.ReactNode;
}

function Legend({
  color,
  label,
  onClick,
}: {
  color: string;
  label: string;
  onClick?: () => void;
}) {
  return (
    <Box
      component={onClick ? "button" : "span"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        p: 0,
        border: 0,
        bgcolor: "transparent",
        font: "inherit",
        color: "text.secondary",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { color: "text.primary", textDecoration: "underline" } : undefined,
      }}
    >
      <Box
        component="span"
        sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }}
      />
      <Typography
        variant="caption"
        component="span"
        sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Employees processed out of the employees a scope covers, as a two-tone bar
 * (successes, then errors) with a "N of M" caption and a legend that names only
 * what is non-zero. A scope without a total yet says so in words instead.
 */
export default function EmployeeProgressBar({
  success,
  error,
  total,
  pendingLabel = "range pending",
  tooltip,
  extra,
  onErrorsClick,
  showPercent = true,
  leading,
}: EmployeeProgressBarProps) {
  const processed = success + error;
  const withLeading = (right: React.ReactNode) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
        {leading}
      </Box>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "baseline", gap: 1 }}>{right}</Box>
    </Box>
  );
  let content: JSX.Element;
  if (total == null) {
    const note = (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
        {pendingLabel}
        {processed > 0 ? ` - ${processed.toLocaleString()} processed` : ""}
      </Typography>
    );
    content = leading ? withLeading(note) : note;
  } else if (total <= 0) {
    const note = (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
        nothing to process
      </Typography>
    );
    content = leading ? withLeading(note) : note;
  } else {
    // 100% means the last employee is done, never a rounded 99.6%: an operator
    // reads a full bar as "finished" and starts the next step on it.
    const complete = processed >= total;
    const pct = complete ? 100 : Math.min(99, Math.floor((processed / total) * 100));
    const okPct = Math.min(100, (success / total) * 100);
    const errPct = Math.max(0, Math.min(100 - okPct, (error / total) * 100));
    const numbers = (
      <Typography
        variant={leading ? "body2" : "caption"}
        component="span"
        sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, color: processed ? "text.primary" : "text.disabled" }}
        >
          {processed.toLocaleString()}
        </Box>
        <Box component="span" sx={{ color: "text.disabled" }}>
          {" "}
          of {total.toLocaleString()}
        </Box>
      </Typography>
    );
    const percent = showPercent ? (
      <Typography
        variant={leading ? "body2" : "caption"}
        component="span"
        sx={{
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
          fontWeight: complete ? 700 : 500,
          color: complete ? "success.main" : "text.secondary",
        }}
      >
        {pct}%
      </Typography>
    ) : null;
    content = (
      <>
        {leading ? (
          withLeading(
            <>
              {numbers}
              {percent}
            </>,
          )
        ) : (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
            {numbers}
            {percent}
          </Box>
        )}
        <Box
          sx={{
            mt: leading ? 0.75 : 0.4,
            height: leading ? 8 : 6,
            borderRadius: 4,
            overflow: "hidden",
            display: "flex",
            bgcolor: (theme) =>
              alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.14 : 0.08),
          }}
        >
          <Box sx={{ width: `${okPct}%`, bgcolor: "success.main", transition: "width .3s ease" }} />
          <Box sx={{ width: `${errPct}%`, bgcolor: "error.main", transition: "width .3s ease" }} />
        </Box>
        {(success > 0 || error > 0 || extra) && (
          <Box sx={{ display: "flex", gap: 1.25, mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
            {success > 0 && (
              <Legend color="success.main" label={`${success.toLocaleString()} success`} />
            )}
            {error > 0 && (
              <Legend
                color="error.main"
                label={`${error.toLocaleString()} ${error === 1 ? "error" : "errors"}`}
                onClick={onErrorsClick}
              />
            )}
            {extra && (
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2 }}>
                {extra}
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  }
  const body = <Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>{content}</Box>;
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
}
